/**
 * Sync run detail — contextual drill-down from Sync History.
 * Loads `GET /api/v1/sync/history/{runId}`; Back returns to the list via
 * history when safe (preserving filters/scroll).
 *
 * Layout: operational run report (summary → compact details), not a form.
 */
import { el, mount, type ElChild } from "../utils/dom";
import { navigateBack } from "../app/navigation";
import { renderLoadingState, renderErrorState } from "../components/states";
import { renderStatusText, runStatusTone } from "../components/statusText";
import { HELP, labelWithHelp, renderInfoTooltip } from "../components/tooltip";
import { absoluteTime, duration, EMPTY, entityLabel, formatNumber } from "../utils/format";
import { getSyncHistoryDetail } from "../services/sync";
import { ApiError } from "../services/apiClient";
import type { SyncHistoryRow } from "../types/api";

export class SyncRunDetailPage {
  private body = el("div", {}, [renderLoadingState("Loading sync run…")]);

  constructor(private id: string) {}

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    mount(
      outlet,
      el("div", { className: "run-report" }, [
        renderRunReportHeader(this.id, () => void this.load(), navigate),
        this.body,
      ])
    );
    void this.load();
  }

  destroy(): void {}

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading sync run…"));
    try {
      const row = await getSyncHistoryDetail(this.id);
      mount(this.body, el("div", { className: "panel panel--standard run-report__panel" }, [renderRunDetail(row)]));
    } catch (err) {
      mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
    }
  }
}

function renderRunReportHeader(
  id: string,
  onRefresh: () => void,
  navigate: (path: string) => void
): HTMLElement {
  const back = el(
    "button",
    {
      className: "page-header__back run-report__back",
      type: "button",
      title: "Back",
      "aria-label": "Back",
      onclick: () => navigateBack(navigate, "/sync/history"),
    },
    [
      el("span", { className: "page-header__back-icon", "aria-hidden": "true" }, ["‹"]),
      el("span", { className: "run-report__back-label" }, ["Back"]),
    ]
  );

  return el("div", { className: "run-report__header" }, [
    el("div", { className: "run-report__chrome" }, [
      back,
      el(
        "button",
        { className: "btn btn-ghost btn-sm", type: "button", onclick: onRefresh },
        ["Refresh"]
      ),
    ]),
    el("h1", { className: "page-header__title run-report__title" }, [`Run #${id}`]),
    el("p", { className: "page-header__subtitle" }, [
      "What ran, when it finished, and how the run ended.",
    ]),
  ]);
}

export function renderRunDetail(row: SyncHistoryRow): HTMLElement {
  const tone = runStatusTone(row.status);

  const statusValue =
    row.status === "partial"
      ? el("span", { className: "label-with-help" }, [
          renderStatusText(tone.label, tone.tone, { pulse: tone.pulse }),
          renderInfoTooltip(HELP.partial),
        ])
      : renderStatusText(tone.label, tone.tone, { pulse: tone.pulse });

  const durationText = row.startedAt && row.completedAt ? duration(row.startedAt, row.completedAt) : EMPTY;

  const metaItems: HTMLElement[] = [
    metaItem([
      el("span", { className: "tnum" }, [formatNumber(row.recordsProcessed)]),
      document.createTextNode(" records processed"),
    ]),
    metaItem([
      el("span", { className: "tnum" }, [durationText]),
      document.createTextNode(" duration"),
    ]),
  ];

  if (row.trigger) {
    metaItems.push(
      metaItem([
        el("span", { className: "label-with-help" }, [
          `${triggerDisplay(row.trigger)} triggered`,
          renderInfoTooltip(HELP.trigger),
        ]),
      ])
    );
  }

  const errorField = detailField(
    "Error message",
    el(
      "span",
      { className: row.error ? "run-report__error run-report__error--present" : "run-report__error" },
      [row.error ?? EMPTY]
    )
  );

  // Paired for a 2-column metadata grid:
  // Run ID / Records skipped · Started / Finished · Last Synced / Error
  // (Timesheet window end slots beside Error when Last Synced is absent.)
  const gridCells: HTMLElement[] = [
    detailField("Run ID", el("span", { className: "mono tnum" }, [`#${row.id}`])),
    detailField(
      labelWithHelp("Records skipped", HELP.skipped),
      el("span", { className: "tnum" }, [formatNumber(row.recordsSkipped)])
    ),
    detailField("Started", timestampValue(row.startedAt)),
    detailField("Finished", timestampValue(row.completedAt)),
  ];

  if (row.kekaLastModifiedWatermark) {
    gridCells.push(
      detailField(labelWithHelp("Last Synced", HELP.watermark), timestampValue(row.kekaLastModifiedWatermark))
    );
    gridCells.push(errorField);
    if (row.timesheetWindowEnd) {
      const windowField = detailField("Timesheet window end", timestampValue(row.timesheetWindowEnd));
      windowField.classList.add("run-report__field--span");
      gridCells.push(windowField);
    }
  } else {
    if (row.timesheetWindowEnd) {
      gridCells.push(detailField("Timesheet window end", timestampValue(row.timesheetWindowEnd)));
    }
    gridCells.push(errorField);
    if (gridCells.length % 2 === 1) {
      errorField.classList.add("run-report__field--span");
    }
  }

  return el("div", { className: "run-report__body" }, [
    el("section", { className: "run-report__summary", "aria-label": "Run summary" }, [
      el("div", { className: "run-report__summary-identity" }, [
        el("p", { className: "run-report__entity" }, [entityLabel(row.entity)]),
        el("div", { className: "run-report__status" }, [statusValue]),
      ]),
      el("div", { className: "run-report__meta" }, metaItems),
    ]),
    el("section", { className: "run-report__details", "aria-label": "Run details" }, [
      el("h2", { className: "run-report__details-title" }, ["Details"]),
      el("div", { className: "run-report__details-grid" }, gridCells),
    ]),
  ]);
}

function metaItem(children: ElChild[]): HTMLElement {
  return el("span", { className: "run-report__meta-item" }, children);
}

function detailField(label: ElChild, value: HTMLElement): HTMLElement {
  return el("div", { className: "run-report__field" }, [
    el("div", { className: "run-report__field-label" }, [label]),
    el("div", { className: "run-report__field-value" }, [value]),
  ]);
}

function timestampValue(iso: string | null | undefined): HTMLElement {
  return el("span", { className: "run-report__timestamp", title: iso ?? undefined }, [
    absoluteTime(iso),
  ]);
}

/** Present trigger values for the summary meta line without inventing new kinds. */
function triggerDisplay(trigger: string): string {
  if (!trigger) return EMPTY;
  return trigger.charAt(0).toUpperCase() + trigger.slice(1);
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
